import { ChangeDetectionStrategy, Component, HostBinding, Injector, OnInit, ViewEncapsulation } from '@angular/core';
import { ObjectTypes, SideListOptionType } from '../../../shared/types';
import { FiFormService } from '../../fi-form.service';
import { FiStateNames } from '../../../shared/fi-state-proxy/fi-state-names';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { IFormReport } from '@fi-shared/fi-form-shared/fi-form-shared';
import { IItemsListAction2, ISideListOptionsItem } from '@fi-shared/fi-items-list/fi-items-list';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { PublicationItemStatus } from '@fi-shared/fi-edit-publication/publication-item-status';
import { FiDateFormatOptions } from '@fi-shared/utils';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { IItemsListAction } from '@fi-app/components/items-list/items-list';
import { FiFormActionsService } from '@fi-app/fi-form/fi-form-actions.service';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { FiFormHelperService } from '@fi-shared/fi-form-shared/fi-form-helper.service';

@Component({
    selector: 'fi-form-report-list',
    templateUrl: './fi-form-report-list.component.html',
    styleUrls: ['./fi-form-report-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-form-report-list' },
    standalone: false,
})
export class FiFormReportListComponent extends FiRemoteStateFullDataSource<IFormReport> implements OnInit {
    defaultSortColumnName = 'Name';
    defaultSortDirection = false;
    objectType = ObjectTypes.Form;
    itemsListType = FiItemsListType.FormReportListData;

    sideListOptions: ISideListOptionsItem[] = [];

    formAccessRight: boolean;
    actions: IItemsListAction<IFormReport>[];
    topActions: IItemsListAction2<IFormReport>[];

    @HostBinding('class.mobile-view') get isMobileView(): boolean {
        return this.detectDeviceService.isMobilePhone();
    }

    constructor(
        injector: Injector,
        private formService: FiFormService,
        private formHelperService: FiFormHelperService,
        private detectDeviceService: DetectDevicesService,
        private fiFormActionsService: FiFormActionsService,
        private fiFormatDateService: FiFormatDateService,
        private stateAccessCheckService: StateAccessCheckService,
    ) {
        super(injector);
    }

    ngOnInit() {
        this.formAccessRight = this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.Form.Add);

        this.initSideList();
        this.initOptionsColumnActions();
        this.initTopActions();
    }

    private initOptionsColumnActions() {
        this.actions = [];

        const deleteAnswersAction: IItemsListAction2<IFormReport> = {
            title: this.appTextService.get('checklist.delete_responses'),
            disabled: (formReport) =>
                formReport.TotalSubmissions === 0 ||
                !this.formHelperService.isEditAllowedByModuleRights() ||
                !formReport.HasEditAccess,
            onClick: (formReport) => {
                this.deleteFormAnswers([formReport]);
            },
        };

        if (this.formAccessRight) {
            this.actions = [
                this.fiFormActionsService.getViewReportAction(),
                this.fiFormActionsService.getEditFormAction(),
                deleteAnswersAction,
            ];
        }
    }

    private deleteFormAnswers(formReports: IFormReport[]) {
        this.fiFormActionsService.doDeleteFormAnswers(formReports, this.getAnswersCount(formReports)).then((r) => {
            if (r.result) {
                formReports.forEach((formReport) => {
                    formReport.TotalSubmissions = formReport.ActiveColleagues = 0;
                    formReport.AnswerSetIds = [];
                });
                this.cdr.markForCheck();
            }
        });
    }

    private getAnswersCount(formReports: IFormReport[]) {
        const totalAnswersCount = formReports
            .map((formReport) => formReport.TotalSubmissions)
            .reduce((a, b) => a + b, 0);
        return totalAnswersCount;
    }

    private initTopActions() {
        const deleteAction: IItemsListAction2<IFormReport> = {
            title: this.appTextService.get('form.delete_form'),
            onClick: () => {
                this.fiFormActionsService.doDeleteForms(this.selectedItems(), this.getPublicMethods());
            },
        };
        const deleteAnswersAction: IItemsListAction2<IFormReport> = {
            title: this.appTextService.get('checklist.delete_responses'),
            disabled: () => {
                return this.getAnswersCount(this.selectedItems()) === 0;
            },
            onClick: () => {
                this.deleteFormAnswers(this.selectedItems());
            },
        };
        this.topActions = [deleteAnswersAction, deleteAction];
    }

    private initSideList() {
        this.sideListOptions = [
            { value: SideListOptionType.SharedWithMe, text: this.appTextService.get('form.all_reports') },
            { value: SideListOptionType.Active, text: this.appTextService.get('form.open_forms') },
            { value: SideListOptionType.Expired, text: this.appTextService.get('form.ended_forms') },
        ];
    }

    loadListDataFn(loadArgs: ILoadDataArgs) {
        const parameter = this.getItemsListParameter(loadArgs);
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
        };
        return this.formService.getFormReportsListData(parameter);
    }

    isNotVisited(formReport: IFormReport) {
        return this.formService.isFormReportUnseen(formReport.AnswerSetIds);
    }

    getFormStatus(formReport: IFormReport) {
        switch (formReport.FormStatus) {
            case PublicationItemStatus.Active:
                return this.appTextService.get('common.open');
            case PublicationItemStatus.Ended:
                return this.appTextService.get('common.closed');
            case PublicationItemStatus.NotShared:
                return this.appTextService.get('common.not_shared');
        }
    }

    getFormStatusClass(formReport: IFormReport) {
        if (formReport.FormStatus === PublicationItemStatus.Ended) return 'CcmListItemRed';

        if (formReport.FormStatus === PublicationItemStatus.Active) return 'CcmListItemGreen';

        return '';
    }

    getLastAnswerDate(formReport: IFormReport) {
        return formReport.MaxAnswerSetCreatedOn
            ? this.fiFormatDateService.formatDate(formReport.MaxAnswerSetCreatedOn, FiDateFormatOptions)
            : '-';
    }

    getActiveColleaguesText(formReport: IFormReport) {
        return formReport.ActiveColleagues + '/' + formReport.FormIsSharedWith;
    }

    getUnseenAnswersCount(formReport: IFormReport) {
        const unseenAnswersCount = this.formService.getUnseenFormReportAnswersCount(formReport.AnswerSetIds);
        return unseenAnswersCount > 0 ? unseenAnswersCount : undefined;
        return 22;
    }
}
